import React, { useCallback } from 'react';
// eslint-disable-next-line @townsquare/rules/no-restricted-imports -- Bootstrap error boundary intentionally uses react-error-boundary directly
import { ErrorBoundary } from 'react-error-boundary';

import { BadRequestError, ForbiddenError, NotFound as NotFoundError } from '@townsquare/error-classes';
import { LoadingState } from '@townsquare/loading-state';
import { logger } from '@townsquare/logging/logger';
import { userAccessRedirectContextHolder } from '@townsquare/router/user-access';

import { ErrorScreen, ForbiddenScreen, NotFoundScreen } from './scenarios';

type BootstrapErrorBoundaryProps = {
	onError?: () => void;
};

export const BootstrapErrorBoundary = (props: React.PropsWithChildren<BootstrapErrorBoundaryProps>) => {
	const { children, onError } = props;

	const handleOnError = useCallback(
		(error: any) => {
			if (
				!(
					error instanceof ForbiddenError ||
					error instanceof NotFoundError ||
					(error instanceof BadRequestError && error.statusCode === 401)
				)
			) {
				logger.error('Bootstrap error boundary', { extra: { error } });
			}
			onError?.();
		},
		[onError],
	);

	return (
		<ErrorBoundary
			onError={handleOnError}
			fallbackRender={({ error }) => {
				if (error instanceof ForbiddenError) {
					if (userAccessRedirectContextHolder.shouldRedirectToProjectAppUserAccessFlow()) {
						window.location.href = userAccessRedirectContextHolder.buildProjectAppUserAccessFlowUrl();
						return <LoadingState />;
					}

					return <ForbiddenScreen />;
				}

				if (error instanceof NotFoundError) {
					return <NotFoundScreen />;
				}

				return <ErrorScreen />;
			}}
		>
			{children}
		</ErrorBoundary>
	);
};
